/**
 * 1.4.0 asset groups, stream G2: Assign for delivery… for a whole group (portal.assign): the one-placeholder dialog's
 * fields (company or user, spec, due date in a time zone, internal contact, notes, extra items) with the preview's
 * counts before confirming ("12 will be assigned; 3 have media; 1 already assigned"), then the per-member outcome.
 * onlyUnassigned: the group page's Assign the unassigned…, prefilled from the group's latest group assignment.
 * Mounted on the group page and on a group row in the Grouped view (G3's GroupedView.tsx). Renders nothing without
 * portal.assign. G2's file.
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Plus, Trash2, Truck } from 'lucide-react';
import { ITEM_KIND_PRESET, ITEM_KINDS, ITEM_TYPE_PRESETS, type DeliveryItemTemplate, type GroupAssignmentOutcomeKind, type ItemKind } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useMe } from '../../api/hooks';
import { useAssignmentChoices } from '../../api/hooks/assignments';
import { useGroupAssignment, useGroupAssignmentPreview, type GroupAssignmentAnswer, type GroupAssignmentPrevious } from '../../api/hooks/groups-admin';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { browserZone, isoToZoned, timeZones, zonedToIso } from '../assignments/zoned';

type ExtraKind = Exclude<ItemKind, 'media'>;
const EXTRA_KINDS = ITEM_KINDS.filter((k): k is ExtraKind => k !== 'media');
const KIND_LABEL: Record<ExtraKind, string> = { document: 'Document', subtitles: 'Subtitles', image: 'Image', audio: 'Audio', other: 'Other' };
interface ItemDraft { key: string; kind: ExtraKind; label: string; description: string; types: string; required: boolean }
let seq = 0;
const draftOf = (i: DeliveryItemTemplate): ItemDraft => ({
  key: `g${++seq}`, kind: i.kind as ExtraKind, label: i.label, description: i.description ?? '', types: (i.acceptedTypes ?? []).join(', '), required: i.required ?? true,
});
const typesOf = (s: string) => [...new Set(s.split(/[\s,;]+/).map((x) => x.trim().replace(/^\./, '').toLowerCase()).filter(Boolean))];
const typesText = (t: readonly string[]) => (t.length ? t.map((x) => `.${x}`).join(' ') : 'any type');
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const OUTCOME_LABEL: Record<GroupAssignmentOutcomeKind, string> = {
  created: 'Assigned', would_create: 'Will be assigned', has_media: 'Has media', already_assigned: 'Already assigned', refused: 'Not assigned',
};

/** "12 will be assigned; 3 have media; 1 already assigned". */
export function previewLine(r: Pick<GroupAssignmentAnswer, 'counts'>, done = false): string {
  const c = r.counts;
  const parts = [done ? `${c.created} assigned` : `${c.would_create} will be assigned`];
  if (c.has_media) parts.push(`${c.has_media} ${c.has_media === 1 ? 'has' : 'have'} media`);
  if (c.already_assigned) parts.push(`${c.already_assigned} already assigned`);
  if (c.refused) parts.push(`${c.refused} not assigned`);
  return parts.join('; ');
}

export function AssignGroupDialog({ groupId, title, onlyUnassigned = false, onClose }: { groupId: string; title: string; onlyUnassigned?: boolean; onClose: () => void }) {
  const choices = useAssignmentChoices().data;
  const preview = useGroupAssignmentPreview(groupId, onlyUnassigned);
  const assign = useGroupAssignment();
  const [result, setResult] = useState<GroupAssignmentAnswer | null>(null);

  const [who, setWho] = useState('');
  const [specId, setSpecId] = useState('');
  const [zone, setZone] = useState(browserZone());
  const [due, setDue] = useState('');
  const [contact, setContact] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([]);
  const [touchedItems, setTouchedItems] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Assign the unassigned…: the group's latest group assignment's settings.
  useEffect(() => {
    const prev: GroupAssignmentPrevious | null | undefined = preview.data?.previous;
    if (!onlyUnassigned || prefilled || !prev || !choices) return;
    setPrefilled(true);
    setWho(prev.portalUserId ? `user:${prev.companyId}:${prev.portalUserId}` : `company:${prev.companyId}`);
    setSpecId(prev.specId ?? '');
    const z = prev.timeZone || browserZone();
    setZone(z);
    setDue(isoToZoned(prev.dueAt, z));
    if (prev.contactUserId) setContact(prev.contactUserId);
    setNotes(prev.notes);
    setItems(prev.items.map(draftOf));
    setTouchedItems(true);
  }, [onlyUnassigned, prefilled, preview.data, choices]);
  useEffect(() => { if (choices && !contact) setContact(choices.me); }, [choices, contact]);
  const spec = choices?.specs.find((s) => s.id === specId) ?? null;
  useEffect(() => { if (!touchedItems) setItems((spec?.defaultItems ?? []).map(draftOf)); }, [spec, touchedItems]);

  const companyId = who.split(':')[1] ?? '';
  const portalUserId = who.startsWith('user:') ? who.split(':')[2]! : null;
  const company = choices?.companies.find((c) => c.id === companyId);
  const zones = useMemo(() => timeZones(zone), [zone]);
  const edit = (key: string, patch: Partial<ItemDraft>) => { setTouchedItems(true); setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x))); };
  const remove = (key: string) => { setTouchedItems(true); setItems((xs) => xs.filter((x) => x.key !== key)); };
  const add = () => { setTouchedItems(true); setItems((xs) => [...xs, draftOf({ kind: 'document', label: '', description: '', acceptedTypes: [...ITEM_TYPE_PRESETS.document], required: true })]); };

  const toAssign = preview.data?.counts.would_create ?? 0;
  const badItem = items.some((i) => !i.label.trim());
  const canSave = Boolean(companyId) && !badItem && !assign.isPending && toAssign > 0;

  async function save() {
    try {
      const r = await assign.mutateAsync({
        groupId, companyId, portalUserId, specId: specId || null, dueAt: due ? zonedToIso(due, zone) : null, timeZone: zone, notes,
        contactUserId: contact || null, onlyUnassigned,
        items: items.map((i) => ({ kind: i.kind, label: i.label.trim(), description: i.description, acceptedTypes: typesOf(i.types), required: i.required })),
      });
      const name = portalUserId ? company?.users.find((u) => u.id === portalUserId)?.name : company?.name;
      toast(`${plural(r.counts.created, 'placeholder')} of ${title} assigned to ${name ?? 'the supplier'}`);
      setResult(r);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  const shown = result ?? preview.data;
  const heading = onlyUnassigned ? `Assign the unassigned of ${title}` : `Assign ${title} for delivery`;
  if (result) {
    return (
      <Modal title={heading} wide onClose={onClose} actions={<button className="btn primary" onClick={onClose} data-testid="group-assign-done">Close</button>}>
        <div className="group-assign" data-testid="group-assign-result">
          <p className="group-assign-summary"><CheckCircle2 size={15} /> {previewLine(result, true)}</p>
          <OutcomeList result={result} />
        </div>
      </Modal>
    );
  }
  return (
    <Modal title={heading} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!canSave} onClick={() => void save()} data-testid="group-assign-save">
        {assign.isPending ? <Loader2 size={14} className="spin" /> : <Truck size={14} />} Assign {toAssign || ''}
      </button>
    </>}>
      <div className="assign-dialog group-assign" data-testid="group-assign-dialog">
        <div className="group-assign-preview" data-testid="group-assign-preview" aria-live="polite">
          {preview.isLoading && <span className="muted"><Loader2 size={13} className="spin" /> Counting the placeholders…</span>}
          {preview.isError && <span className="danger-text">{errorMessage(preview.error)}</span>}
          {shown && (
            <>
              <strong>{previewLine(shown)}</strong>
              <span className="faint">
                {onlyUnassigned ? 'Only members without a delivery are assigned.' : 'Every placeholder of the group and its sub-groups; members with media or an open assignment are skipped.'}
                {onlyUnassigned && preview.data?.previous ? ' The settings of the last group assignment are filled in.' : ''}
              </span>
              {shown.members.length > 0 && (
                <details>
                  <summary>Show the {plural(shown.members.length, 'member')}</summary>
                  <OutcomeList result={shown} />
                </details>
              )}
            </>
          )}
        </div>

        <div className="assign-grid">
          <div className="field">
            <label htmlFor="gassign-who">Deliver by <span className="req">*</span></label>
            <select id="gassign-who" className="select" value={who} onChange={(e) => setWho(e.target.value)} data-testid="group-assign-who">
              <option value="">Choose a company or user…</option>
              {(choices?.companies ?? []).map((c) => (
                <optgroup key={c.id} label={c.name}>
                  <option value={`company:${c.id}`}>{c.name}: every user</option>
                  {c.users.map((u) => <option key={u.id} value={`user:${c.id}:${u.id}`}>{u.name} ({u.email}){u.invited ? ', invited' : ''}</option>)}
                </optgroup>
              ))}
            </select>
            {choices && !choices.companies.length && <span className="help">No companies yet. An administrator adds them under Administration → Media portal.</span>}
            {company && !portalUserId && <span className="help">Every user of {company.name} sees them and is emailed.</span>}
          </div>
          <div className="field">
            <label htmlFor="gassign-spec">Delivery spec</label>
            <select id="gassign-spec" className="select" value={specId} onChange={(e) => setSpecId(e.target.value)} data-testid="group-assign-spec">
              <option value="">No spec (any media)</option>
              {(choices?.specs ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {spec?.description && <span className="help">{spec.description}</span>}
          </div>
          <div className="field">
            <label htmlFor="gassign-due">Due</label>
            <div className="assign-due">
              <input id="gassign-due" className="input" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="group-assign-due" />
              <select className="select" aria-label="Time zone" value={zone} onChange={(e) => {
                const iso = due ? zonedToIso(due, zone) : null;
                setZone(e.target.value);
                if (iso) setDue(isoToZoned(iso, e.target.value));
              }}>
                {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <span className="help">The same due date for every placeholder; the supplier's email shows it in this time zone.</span>
          </div>
          <div className="field">
            <label htmlFor="gassign-contact">Internal contact</label>
            <select id="gassign-contact" className="select" value={contact} onChange={(e) => setContact(e.target.value)}>
              {(choices?.contacts ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.id === choices?.me ? ' (you)' : ''}</option>)}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="gassign-notes">Notes to the supplier</label>
          <textarea id="gassign-notes" className="input" rows={3} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything the production company should know about this season: versions, audio layout, deadlines" data-testid="group-assign-notes" />
        </div>

        <div className="assign-items">
          <div className="assign-items-head">
            <h3>Requested items, for each placeholder</h3>
            <button type="button" className="btn ghost sm" onClick={add} disabled={items.length >= 30}><Plus size={13} /> Add item</button>
          </div>
          <div className="assign-item media">
            <div className="assign-item-title"><strong>Media</strong><span className="pill accent">Always requested</span></div>
            <span className="faint">{spec ? `${spec.name}: ${typesText(spec.acceptedTypes)}` : 'Any media file PRISM can ingest'}</span>
          </div>
          {items.map((i, n) => (
            <div className="assign-item" key={i.key}>
              <div className="assign-item-row">
                <select className="select" aria-label="Kind" value={i.kind}
                  onChange={(e) => { const kind = e.target.value as ExtraKind; edit(i.key, { kind, types: ITEM_TYPE_PRESETS[ITEM_KIND_PRESET[kind]].join(', ') }); }}>
                  {EXTRA_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
                <input className="input" aria-label="Label" placeholder="Label, e.g. Final manuscript" value={i.label} maxLength={200}
                  onChange={(e) => edit(i.key, { label: e.target.value })} aria-invalid={!i.label.trim()} />
                <label className="assign-required"><input type="checkbox" checked={i.required} onChange={(e) => edit(i.key, { required: e.target.checked })} /> Required</label>
                <button type="button" className="btn ghost icon sm" aria-label={`Remove item ${n + 1}`} onClick={() => remove(i.key)}><Trash2 size={13} /></button>
              </div>
              <div className="assign-item-row">
                <input className="input" aria-label="Short description" placeholder="Short description" value={i.description} maxLength={2000}
                  onChange={(e) => edit(i.key, { description: e.target.value })} />
                <input className="input assign-types" aria-label="Accepted types" placeholder="Any type" value={i.types}
                  onChange={(e) => edit(i.key, { types: e.target.value })} title="File extensions, separated by commas; empty: any type" />
              </div>
            </div>
          ))}
          {!items.length && <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>Only the media. Add items for manuscripts, flyers, subtitles and so on.</p>}
        </div>
      </div>
    </Modal>
  );
}

function OutcomeList({ result }: { result: GroupAssignmentAnswer }) {
  return (
    <ul className="group-assign-members" data-testid="group-assign-members">
      {result.members.map((m) => (
        <li key={m.assetId} data-outcome={m.outcome}>
          <span className="mono">{m.assetCode}</span>
          <span className="group-assign-title">{m.title}</span>
          <span className={`pill ${m.outcome === 'created' || m.outcome === 'would_create' ? 'accent' : m.outcome === 'refused' ? 'danger' : ''}`} title={m.message}>{OUTCOME_LABEL[m.outcome]}</span>
        </li>
      ))}
    </ul>
  );
}

export function AssignGroupForDeliveryButton({ groupId, title, onlyUnassigned = false, compact = false }: { groupId: string; title: string; onlyUnassigned?: boolean; compact?: boolean }) {
  const can = useMe().data?.permissions.includes('portal.assign') ?? false;
  const [open, setOpen] = useState(false);
  if (!can) return null;
  const label = onlyUnassigned ? 'Assign the unassigned…' : 'Assign for delivery…';
  return (
    <>
      <button className={`btn ${compact ? 'ghost icon sm' : 'sm'}`} data-testid={onlyUnassigned ? 'group-assign-unassigned' : 'group-assign'} aria-label={compact ? `${label} ${title}` : undefined}
        title={onlyUnassigned ? 'Assign the placeholders that have no delivery yet, with the settings of the last group assignment' : `Ask a production company to deliver every placeholder of ${title}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        <Truck size={compact ? 13 : 14} />{compact ? null : ` ${label}`}
      </button>
      {open && <AssignGroupDialog groupId={groupId} title={title} onlyUnassigned={onlyUnassigned} onClose={() => setOpen(false)} />}
    </>
  );
}
